/**
 * Service state machine — the only place that decides whether a status change is legal.
 * Service Core calls `assertTransition` before every write; the DB only enforces invariants
 * (driver present when accepted, one live service per driver, ...).
 */
export const SERVICE_STATUSES = [
  'CREATED',
  'COLLECTING_DATA',
  'READY_FOR_DISPATCH',
  'DISPATCHING',
  'ACCEPTED',
  'DRIVER_EN_ROUTE',
  'DRIVER_ARRIVED',
  'IN_PROGRESS',
  'COMPLETED',
  'CLOSED',
  'CANCELLED_BY_CLIENT',
  'CANCELLED_BY_DRIVER',
  'CANCELLED_BY_ADMIN',
  'NO_SHOW',
  'REJECTED',
  'EXPIRED',
  'DISPUTED',
] as const;
export type ServiceStatus = (typeof SERVICE_STATUSES)[number];

export type ActorType = 'USER' | 'DRIVER' | 'CUSTOMER' | 'SYSTEM';

type Rule = { to: ServiceStatus; actors: ActorType[] };

const S: ActorType = 'SYSTEM';
const U: ActorType = 'USER';
const D: ActorType = 'DRIVER';
const C: ActorType = 'CUSTOMER';

const TRANSITIONS: Record<ServiceStatus, Rule[]> = {
  CREATED: [
    { to: 'COLLECTING_DATA', actors: [S, U, C] },
    { to: 'READY_FOR_DISPATCH', actors: [S, U, C] },
    { to: 'CANCELLED_BY_CLIENT', actors: [C, U] },
    { to: 'CANCELLED_BY_ADMIN', actors: [U] },
    { to: 'EXPIRED', actors: [S] },
  ],
  COLLECTING_DATA: [
    { to: 'READY_FOR_DISPATCH', actors: [S, U, C] },
    { to: 'CANCELLED_BY_CLIENT', actors: [C, U] },
    { to: 'CANCELLED_BY_ADMIN', actors: [U] },
    { to: 'EXPIRED', actors: [S] },
  ],
  READY_FOR_DISPATCH: [
    { to: 'DISPATCHING', actors: [S, U] },
    { to: 'ACCEPTED', actors: [U] }, // manual assignment
    { to: 'CANCELLED_BY_CLIENT', actors: [C, U] },
    { to: 'CANCELLED_BY_ADMIN', actors: [U] },
    { to: 'REJECTED', actors: [U, S] },
    { to: 'EXPIRED', actors: [S] },
  ],
  DISPATCHING: [
    { to: 'ACCEPTED', actors: [D, S, U] }, // D = driver accepted an offer (via Dispatch Engine), U = manual assignment
    { to: 'READY_FOR_DISPATCH', actors: [U, S] }, // dispatch stopped (manual queue / restart)
    { to: 'CANCELLED_BY_CLIENT', actors: [C, U] },
    { to: 'CANCELLED_BY_ADMIN', actors: [U] },
    { to: 'REJECTED', actors: [U, S] },
    { to: 'EXPIRED', actors: [S] },
  ],
  ACCEPTED: [
    { to: 'DRIVER_EN_ROUTE', actors: [D, U] },
    { to: 'DRIVER_ARRIVED', actors: [D, U] },
    { to: 'DISPATCHING', actors: [S, U] }, // driver dropped the service -> redispatch
    { to: 'CANCELLED_BY_CLIENT', actors: [C, U] },
    { to: 'CANCELLED_BY_DRIVER', actors: [D] },
    { to: 'CANCELLED_BY_ADMIN', actors: [U] },
  ],
  DRIVER_EN_ROUTE: [
    { to: 'DRIVER_ARRIVED', actors: [D, U] },
    { to: 'DISPATCHING', actors: [S, U] },
    { to: 'CANCELLED_BY_CLIENT', actors: [C, U] },
    { to: 'CANCELLED_BY_DRIVER', actors: [D] },
    { to: 'CANCELLED_BY_ADMIN', actors: [U] },
  ],
  DRIVER_ARRIVED: [
    { to: 'IN_PROGRESS', actors: [D, U] },
    { to: 'NO_SHOW', actors: [D, U] },
    { to: 'CANCELLED_BY_CLIENT', actors: [C, U] },
    { to: 'CANCELLED_BY_DRIVER', actors: [D] },
    { to: 'CANCELLED_BY_ADMIN', actors: [U] },
  ],
  IN_PROGRESS: [
    { to: 'COMPLETED', actors: [D, U] },
    { to: 'CANCELLED_BY_ADMIN', actors: [U] },
    { to: 'DISPUTED', actors: [U, C] },
  ],
  COMPLETED: [
    { to: 'CLOSED', actors: [S, U] },
    { to: 'DISPUTED', actors: [U, C] },
  ],
  DISPUTED: [{ to: 'CLOSED', actors: [U] }],
  NO_SHOW: [{ to: 'DISPUTED', actors: [U, C] }],
  CLOSED: [],
  CANCELLED_BY_CLIENT: [],
  CANCELLED_BY_DRIVER: [],
  CANCELLED_BY_ADMIN: [],
  REJECTED: [],
  EXPIRED: [],
};

export const TERMINAL_STATUSES: readonly ServiceStatus[] = SERVICE_STATUSES.filter((s) => TRANSITIONS[s].length === 0);

/** Statuses in which a driver is bound to the service (one at a time per driver). */
export const DRIVER_ACTIVE_STATUSES: readonly ServiceStatus[] = ['ACCEPTED', 'DRIVER_EN_ROUTE', 'DRIVER_ARRIVED', 'IN_PROGRESS'];

/** Statuses still waiting for a driver. */
export const PENDING_STATUSES: readonly ServiceStatus[] = ['CREATED', 'COLLECTING_DATA', 'READY_FOR_DISPATCH', 'DISPATCHING'];

export const CANCELLED_STATUSES: readonly ServiceStatus[] = ['CANCELLED_BY_CLIENT', 'CANCELLED_BY_DRIVER', 'CANCELLED_BY_ADMIN'];

/**
 * Why the operation cancelled a trip, as the customer is told (WhatsApp). The operator's free-text
 * reason stays an internal note; only one of these codes reaches the customer.
 */
export const CUSTOMER_CANCEL_REASONS = ['NO_DRIVERS', 'ADDRESS_INVALID', 'CUSTOMER_REQUEST', 'DUPLICATE', 'OUT_OF_AREA', 'OTHER'] as const;
export type CustomerCancelReason = (typeof CUSTOMER_CANCEL_REASONS)[number];

export function isTerminal(status: ServiceStatus): boolean {
  return TERMINAL_STATUSES.includes(status);
}

export function canTransition(from: ServiceStatus, to: ServiceStatus, actor: ActorType): boolean {
  return TRANSITIONS[from].some((r) => r.to === to && r.actors.includes(actor));
}

/**
 * What an operator may change on behalf of the driver (`POST /admin/services/:id/status`), e.g. when
 * the driver's phone died. The other USER transitions belong to operations that carry their side
 * effects: assigning (`/assign`), searching again (`/redispatch`) and cancelling (`/cancel`).
 */
export const OPERATOR_STATUS_CHANGES: Readonly<Partial<Record<ServiceStatus, readonly ServiceStatus[]>>> = {
  ACCEPTED: ['DRIVER_EN_ROUTE', 'DRIVER_ARRIVED'],
  DRIVER_EN_ROUTE: ['DRIVER_ARRIVED'],
  DRIVER_ARRIVED: ['IN_PROGRESS', 'NO_SHOW'],
  IN_PROGRESS: ['COMPLETED', 'DISPUTED'],
  COMPLETED: ['CLOSED', 'DISPUTED'],
  DISPUTED: ['CLOSED'],
};

export function isOperatorStatusChange(from: ServiceStatus, to: ServiceStatus): boolean {
  return OPERATOR_STATUS_CHANGES[from]?.includes(to) ?? false;
}

export class InvalidTransitionError extends Error {
  readonly code = 'INVALID_TRANSITION';
  constructor(
    readonly from: ServiceStatus,
    readonly to: ServiceStatus,
    readonly actor: ActorType,
  ) {
    super(`Transition ${from} -> ${to} is not allowed for ${actor}`);
  }
}

export function assertTransition(from: ServiceStatus, to: ServiceStatus, actor: ActorType): void {
  if (!canTransition(from, to, actor)) throw new InvalidTransitionError(from, to, actor);
}

/** Timestamp column that records reaching a given status (if any). */
export function timestampFieldFor(status: ServiceStatus): string | null {
  switch (status) {
    case 'DISPATCHING':
      return 'dispatch_started_at';
    case 'ACCEPTED':
      return 'accepted_at';
    case 'DRIVER_ARRIVED':
      return 'arrived_at';
    case 'IN_PROGRESS':
      return 'started_at';
    case 'COMPLETED':
      return 'completed_at';
    case 'CANCELLED_BY_CLIENT':
    case 'CANCELLED_BY_DRIVER':
    case 'CANCELLED_BY_ADMIN':
      return 'cancelled_at';
    default:
      return null;
  }
}
